import ValuerAmendFieldListHandler from "./ValuerAmendFieldListHandler";
import {useEffect, useState} from "react";
import valuer_api from "../../api/valuer";
import ValuerAmendRadioButton from "./ValuerAmendRadioButton";


const ValuerAmendRadio = (props) => {

    // For Saving
    const summaryId = props.summaryId
    const section = props.sectionName
    const identifier = props.id

    // Required
    const field = props.field
    const options = props.options ? props.options : [];

    // Optional
    const flex = props.flex ? props.flex : false
    const optionsFlex = props.optionsFlex ? props.optionsFlex : false
    const showValueDependantFields = props.showValueDependantFields ? props.showValueDependantFields : false
    const optionRequiresDescription = props.optionRequiresDescription ? props.optionRequiresDescription : false
    const descriptionIdentifier = props.descriptionIdentifier ? props.descriptionIdentifier : ""

    const fieldInfo = props.fieldInfo ? props.fieldInfo : false
    const infoColor = props.infoColor ? props.infoColor : ""

    // Value
    const [value, setValue] = useState(props.value !== undefined ? props.value : null)
    const [descriptionValue, setDescriptionValue] = useState(null)

    // Additional Fields
    const sectionValues = props.sectionValues ? props.sectionValues : {}
    const valueDependantFields = props.valueDependantFields ? props.valueDependantFields : []

    // Saving Tracker
    const UpdateParentValue = props.UpdateParentValue
    const AddToQueue = props.AddToQueue
    const RemoveFromQueue = props.RemoveFromQueue
    const AddToErroredSaves = props.AddToErroredSaves

    // Error Handling
    const setErrorMessage = props.setErrorMessage
    const setErrorTitle = props.setErrorTitle

    useEffect(() => {
        const normalize = (v) => {
            try {
                if (typeof v === 'string') v = JSON.parse(v);
            } catch {}
            return (v && typeof v === 'object') ? v.value : v;
        };
        if (normalize(props.value) !== normalize(value)) {
            setValue(props.value);
        }
    }, [props.value]);


    useEffect(() => {
        if (props.descriptionValue !== undefined) setDescriptionValue(props.descriptionValue)
    }, [props.descriptionValue])

    const clearPlanningPermissionCustomList = async ({
                                                         summaryId,
                                                         section,
                                                         nestedValues,
                                                         UpdateParentValue,
                                                         AddToQueue,
                                                         RemoveFromQueue,
                                                         AddToErroredSaves,
                                                         setErrorMessage,
                                                         setErrorTitle
                                                     }) => {
        if (!nestedValues || typeof nestedValues !== "object") return

        const identifiers = [
            "planning-permission-custom-list-date",
            "planning-permission-custom-list-text"
        ]

        const rowIds = Object.keys(nestedValues)

        for (let row of rowIds) {
            const updateTime = Date.now()
            for (let identifier of identifiers) {
                AddToQueue(section, identifier, updateTime)
                UpdateParentValue(identifier, null, row)  // clear in parent

                try {
                    await valuer_api.ValuerAmendPut(summaryId, section, identifier, {
                        value: null,
                        row: row
                    })
                    RemoveFromQueue(section, identifier, updateTime)
                } catch (e) {
                    AddToErroredSaves(section, identifier, updateTime)
                    setErrorTitle("Failed to Clear")
                    setErrorMessage("Could not remove old custom planning entries.")
                }
            }
        }
    }




    const UpdateValue = (_value, _label) => {
        if (!summaryId || !section || !identifier) return

        // Specific change for 'Environmental Matters' to allow for label and value usage
        const fieldsWithLabelAndValue = [
            "flood-zone",
            "flood-zone-surface-water",
            "flood-zone-rivers-sea"
        ]

        let updateTime = Date.now()
        AddToQueue(section, identifier, updateTime)

        let storedValue = fieldsWithLabelAndValue.includes(identifier)
            ? { label: _label, value: _value }   // ← plain object
            : _value

        setValue(storedValue)

        // Trigger clearing custom list if user selects "No" for Planning Permission
        if (section === "planning-and-conservation-fields" && identifier === "planning-permission" && _value === 0) {
            const customListValues = sectionValues["planning-permission-custom-list"]
            if (customListValues) {
                clearPlanningPermissionCustomList({
                    summaryId,
                    section: "planning-permission-custom-list",
                    nestedValues: customListValues,
                    UpdateParentValue,
                    AddToQueue,
                    RemoveFromQueue,
                    AddToErroredSaves,
                    setErrorMessage,
                    setErrorTitle
                })
                // Trigger rerender of custom-addition-fields by resetting top-level group
                UpdateParentValue("planning-permission-custom-list", null)
            }
        }


        if (section === "planning-and-conservation-fields") {
            const radioToInfoMap = {
                "planning-permission": "planning-permission-text-info",
                "green-belt": "green-text-info",
                "brownfield-site": "brownfield-site-info",
                "listed-building": "listed-building-info",
                "conservation": "conservation-info"
            }

            const defaultTextMap = {
                 "planning-permission-text-info": {
                    0: "No planning applications relevant to the subject property have been found."
                },
                "green-text-info": {
                    1: "The subject property is situated in a green belt.",
                    0: "The subject property is not situated in a green belt."
                },
                "brownfield-site-info": {
                    1: "The subject property has been identified as being brownfield land.",
                    0: "The subject property has not been identified as being brownfield land."
                },
                "listed-building-info": {
                    1: "The subject property is a listed building.",
                    0: "The subject property is not a listed building."
                },
                "conservation-info": {
                    1: "The subject property is in a conservation area.",
                    0: "The subject property is not in a conservation area."
                },
            };

            const pairedInfo = radioToInfoMap[identifier];
            const infoUpdateTime = Date.now();

            if (pairedInfo) {
                let autoText = "";

                if (_value in defaultTextMap[pairedInfo]) {
                    autoText = defaultTextMap[pairedInfo][_value];
                }

                AddToQueue(section, pairedInfo, infoUpdateTime);
                UpdateParentValue(pairedInfo, autoText, undefined);

                valuer_api.ValuerAmendPut(summaryId, section, pairedInfo, { value: autoText })
                    .then(() => {
                        RemoveFromQueue(section, pairedInfo, infoUpdateTime);
                    })
                    .catch(() => {
                        AddToErroredSaves(section, pairedInfo, infoUpdateTime);
                        setErrorTitle("Failed to Save");
                        setErrorMessage("Failed to auto-save freetext info for radio selection.");
                    });
            }
        }


        // --- SOCIAL AND GOVERNANCE FACTORS PATCH ------------------------------
        if (section === "social-and-governance-factors") {
            const radioToTextboxMap = {
                "social-factors": "social-factors-info",
                "governance-factors": "governance-factors-info",
            };

            const defaultTexts = {
                "social-factors": "We are not aware of any adverse social factors that might have a negative impact on the value of the subject property.",
                "governance-factors": "We are not aware of any adverse governance factors that might have a negative impact on the value of the subject property.",
            };

            const targetTextbox = radioToTextboxMap[identifier];
            if (targetTextbox) {
                const updateTime = Date.now();
                const newValue =
                    _value === 0 ? defaultTexts[identifier] : ""; // 0 = No → default text, 1 = Yes → clear

                AddToQueue(section, targetTextbox, updateTime);
                UpdateParentValue(targetTextbox, newValue, undefined);

                valuer_api.ValuerAmendPut(summaryId, section, targetTextbox, { value: newValue })
                    .then(() => {
                        RemoveFromQueue(section, targetTextbox, updateTime);
                    })
                    .catch(() => {
                        AddToErroredSaves(section, targetTextbox, updateTime);
                        setErrorTitle("Failed to Update");
                        setErrorMessage("Could not update textbox for selection change.");
                    });
            }
        }

    // --- END PATCH --------------------------------------------------------


        let body = { "value": storedValue}
        UpdateParentValue(identifier, storedValue, undefined);

        let response = valuer_api.ValuerAmendPut(summaryId, section, identifier, body);
        response.then(
            response => {
                RemoveFromQueue(section, identifier, updateTime)
            }
        ).catch((err) => {
            AddToErroredSaves(section, identifier, updateTime)
            setErrorTitle("Failed to Save")
            setErrorMessage("The request to save a field has failed")
        })
    }

    return (
        <>
            <div className={"valuer-amend-radio-field-container" + (flex ? " inline" : "")} style={{margin: props.disableMargin ? '14px 0' : null}}>
                <div className="valuer-amend-radio-label" dangerouslySetInnerHTML={{__html: field && field}}/>
                {fieldInfo && fieldInfo
                    ? <div className="valuer-amend-field-info" style={{color: infoColor}}> {fieldInfo} </div>
                    : null
                }
                <div className={"valuer-amend-radio-buttons-container" + (optionsFlex ? " inline" : "")}>
                    {options && options.map((option, index) => {

                        return (
                            <ValuerAmendRadioButton key={index}
                                                    value={option.value}
                                                    identifier={identifier}
                                                    textValue={descriptionValue}
                                                    label={option.label}
                                                    summaryId={summaryId}
                                                    section={section}
                                                    selected={value}
                                //== IS REQUIRED DO NOT CHANGE
                                                    inline={props.flex}
                                                    optionRequiresDescription={optionRequiresDescription}
                                                    descriptionIdentifier={descriptionIdentifier}
                                                    descriptionDefault={option.descriptionDefault}
                                                    descriptionMinHeight={option.descriptionMinHeight}
                                                    updateFunction={(val, label) => UpdateValue(val, label)}

                                                    setErrorMessage={setErrorMessage}
                                                    setErrorTitle={setErrorTitle}
                                                    UpdateParentValue={UpdateParentValue}
                                                    AddToQueue={AddToQueue.bind(this)}
                                                    RemoveFromQueue={RemoveFromQueue.bind(this)}
                                                    AddToErroredSaves={AddToErroredSaves.bind(this)}
                            />
                        )
                    })}
                </div>
            </div>
            {valueDependantFields && valueDependantFields.map((fields, idx) => {
                return (
                    <>
                        {value !== undefined && (fields.requirement && value == fields.requirement.value)
                        || value && (fields.antiRequirement && value !== fields.antiRequirement.value)
                            ? <ValuerAmendFieldListHandler fieldList={fields.fields}
                                                           sectionName={section}
                                                           summaryId={summaryId}
                                                           setErrorMessage={setErrorMessage}
                                                           setErrorTitle={setErrorTitle}
                                                           values={sectionValues}
                                                           UpdateParentValue={UpdateParentValue}
                                                           AddToQueue={AddToQueue}
                                                           RemoveFromQueue={RemoveFromQueue}
                                                           AddToErroredSaves={AddToErroredSaves}/>

                            : (showValueDependantFields ?
                                <div className="valuer-amend-disabled-container">
                                    <ValuerAmendFieldListHandler fieldList={fields.fields}
                                                                 sectionName={section}
                                                                 summaryId={summaryId}
                                                                 setErrorMessage={setErrorMessage}
                                                                 setErrorTitle={setErrorTitle}
                                                                 values={sectionValues}
                                                                 UpdateParentValue={UpdateParentValue}
                                                                 AddToQueue={AddToQueue}
                                                                 RemoveFromQueue={RemoveFromQueue}
                                                                 AddToErroredSaves={AddToErroredSaves}/>
                                </div>
                            : null)
                        }
                    </>
                )
            })}
        </>
    )
}

export default ValuerAmendRadio